import React, { createContext, useEffect, useState } from "react";
import moment from "moment";
import { workflowStateApi } from "../services/api";

// Same shape as the Data360 app's DateContext: latest valuation date plus every
// available valuation date, read from the Data360 backend.
const initialData = {
  currentDate: moment().format("MMM DD, YYYY"),
  latestValuationDate: null,
  dates: null,
};

export const DateContext = createContext({ data: initialData, isLoading: true });

// The last known dates are kept in localStorage so the page can start its queries immediately on the next
// visit (stale-while-revalidate); the fresh list replaces them as soon as it arrives.
const CACHE_KEY = "riskFramework.valuationDates.v1";
const readCache = () => {
  try {
    const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
    return cached?.latestValuationDate && Array.isArray(cached?.dates) ? cached : null;
  } catch {
    return null;
  }
};
const writeCache = (value) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(value));
  } catch {
    /* storage unavailable: skip caching */
  }
};

export const DateProvider = ({ children }) => {
  const [data, setData] = useState(() => {
    const cached = readCache();
    return cached ? { ...initialData, ...cached } : initialData;
  });
  const [isLoading, setIsLoading] = useState(() => !readCache());

  useEffect(() => {
    let cancelled = false;
    async function fetchLatestValuationDate() {
      try {
        const response = await workflowStateApi.getDates();
        const rows = response?.data?.rows || [];
        const dates = rows.map((row) => moment.utc(row.valuation_date).format("YYYY-MM-DD"));
        if (!cancelled) {
          const latestValuationDate = rows[0]?.valuation_date
            ? moment.utc(rows[0].valuation_date).format("MMM DD, YYYY")
            : moment().format("MMM DD, YYYY");
          setData((prev) => ({ ...prev, latestValuationDate, dates }));
          if (rows.length) writeCache({ latestValuationDate, dates });
        }
      } catch (error) {
        console.error("Failed to fetch valuation dates:", error.message);
        if (!cancelled) {
          setData((prev) => ({ ...prev, latestValuationDate: moment().format("MMM DD, YYYY") }));
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchLatestValuationDate();
    return () => {
      cancelled = true;
    };
  }, []);

  return <DateContext.Provider value={{ data, isLoading }}>{children}</DateContext.Provider>;
};
